import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Archive, Pencil, Plus, RotateCcw, ArrowDown, ArrowUp, Tag } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { ListRow } from '@/components/ui/Card';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { SectionCard, SettingsEmpty, SettingsLoading } from './settingsChrome';
import {
  useCreateDocumentHighlightTag,
  useDocumentHighlightTags,
  useReorderDocumentHighlightTags,
  useUpdateDocumentHighlightTag,
  type DocumentHighlightTagRow,
} from '@/hooks/useDocumentHighlightTags';
import { HighlightTagFace } from '@/components/documents/HighlightTagFace';
import {
  activeHighlightTags,
  compareHighlightTags,
  firstFreeHighlightTagColor,
  highlightTagColorLabelKey,
  highlightTagDisplayName,
  highlightTagHasDuplicateColors,
  highlightTagInkCss,
  isHighlightTagPaletteToken,
  occupiedHighlightTagColors,
  HIGHLIGHT_TAG_PALETTE,
} from '@/lib/highlightTagColors';
import { fileCategorySwatchClass } from '@/lib/patientFileCategories';

function ColorChoices({
  tags,
  editingId,
  color,
  onColor,
}: {
  tags: readonly DocumentHighlightTagRow[];
  editingId?: string;
  color: string;
  onColor: (token: string) => void;
}) {
  const { t } = useTranslation();
  const taken = occupiedHighlightTagColors(tags, editingId);
  const tokens = [...HIGHLIGHT_TAG_PALETTE] as string[];
  if (!isHighlightTagPaletteToken(color) && !tokens.includes(color)) tokens.push(color);
  const paletteFull = !editingId && tokens.every((token) => taken.has(token));
  const shared = tags.find((row) => row.id !== editingId && row.color_token === color);
  const unnamed = t('settings.highlight_tags.unnamed');
  return (
    <fieldset>
      <legend className={cn(TYPE.label, 'mb-s2 text-ds-text')}>
        {t('settings.highlight_tags.field_color')}
      </legend>
      {paletteFull ? (
        <p className={cn(TYPE.small, 'mb-s3 text-ds-amber-ink')}>
          {t('settings.highlight_tags.colors_full')}
        </p>
      ) : null}
      {shared ? (
        <p className={cn(TYPE.small, 'mb-s3 text-ds-amber-ink')}>
          {t('settings.highlight_tags.color_shared', {
            name: highlightTagDisplayName(
              shared,
              unnamed,
              t(highlightTagColorLabelKey(shared.color_token)),
            ),
          })}
        </p>
      ) : null}
      {!isHighlightTagPaletteToken(color) ? (
        <p className={cn(TYPE.small, 'mb-s3 text-ds-amber-ink')}>
          {t('settings.highlight_tags.legacy_color', {
            color: t(highlightTagColorLabelKey(color)),
          })}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-s2">
        {tokens.map((token) => {
          const blocked = taken.has(token) && token !== color;
          const label = t(highlightTagColorLabelKey(token));
          const owner = tags.find((row) => row.id !== editingId && row.color_token === token);
          const ink = highlightTagInkCss(token);
          const takenLabel = owner
            ? t('settings.highlight_tags.color_taken', {
                name: highlightTagDisplayName(
                  owner,
                  unnamed,
                  t(highlightTagColorLabelKey(owner.color_token)),
                ),
              })
            : '';
          return (
            <button
              key={token}
              type="button"
              disabled={blocked}
              onClick={() => {
                if (blocked) return;
                if (!isHighlightTagPaletteToken(token) && token !== color) return;
                onColor(token);
              }}
              aria-pressed={color === token}
              aria-label={blocked ? `${label}. ${takenLabel}` : label}
              className={cn(
                'flex h-10 items-center gap-s2 rounded-ctl border px-s3',
                blocked && 'cursor-not-allowed opacity-50',
                color === token ? 'border-ds-teal bg-ds-teal-tint' : 'border-ds-field bg-ds-card',
              )}
            >
              <span
                className={cn('h-3.5 w-3.5 rounded-full', !ink && fileCategorySwatchClass(token))}
                style={ink ? { background: ink } : undefined}
                aria-hidden
              />
              <span className={cn(TYPE.control, 'text-ds-text')}>{label}</span>
              {blocked ? (
                <span className={cn(TYPE.meta, 'text-ds-muted')}>
                  {t('settings.highlight_tags.color_busy')}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

function errorCode(err: unknown): string {
  if (!err || typeof err !== 'object' || !('code' in err)) return '';
  return String((err as { code?: string }).code ?? '');
}

function isUniqueNameViolation(err: unknown): boolean {
  return errorCode(err) === '23505';
}

function isColorCheckViolation(err: unknown): boolean {
  return errorCode(err) === '23514';
}

export function HighlightTagsSettings() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { hasPermission } = useAuth();
  const canManage = Boolean(activeClinicId && hasPermission(activeClinicId, 'documents', 'update'));

  const tagsQuery = useDocumentHighlightTags(activeClinicId);
  const create = useCreateDocumentHighlightTag(activeClinicId);
  const update = useUpdateDocumentHighlightTag(activeClinicId);
  const reorder = useReorderDocumentHighlightTags(activeClinicId);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<DocumentHighlightTagRow | null>(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState('amber');

  const tags = tagsQuery.data ?? [];
  const active = activeHighlightTags(tags);
  const archived = tags.filter((row) => row.is_archived).sort((a, b) => compareHighlightTags(a, b));
  const duplicates = highlightTagHasDuplicateColors(tags);

  const openCreate = () => {
    setEditing(null);
    setName('');
    setColor(firstFreeHighlightTagColor(tags) ?? 'amber');
    setShowForm(true);
  };

  const openEdit = (row: DocumentHighlightTagRow) => {
    setEditing(row);
    setName(row.name);
    setColor(row.color_token);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setName('');
    setColor(firstFreeHighlightTagColor(tags) ?? 'amber');
  };

  const saveError = (err: unknown) => {
    if (isUniqueNameViolation(err)) {
      toast.error(t('settings.highlight_tags.toast_duplicate'));
      return;
    }
    if (isColorCheckViolation(err)) {
      toast.error(t('settings.highlight_tags.toast_color_pending'));
      return;
    }
    toast.error(err instanceof Error ? err.message : t('settings.highlight_tags.toast_error'));
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const taken = occupiedHighlightTagColors(tags, editing?.id);
    const keepingCurrent = Boolean(editing && color === editing.color_token);
    if (taken.has(color) && !keepingCurrent) {
      toast.error(t('settings.highlight_tags.toast_color_taken'));
      return;
    }
    if (!keepingCurrent && !isHighlightTagPaletteToken(color)) {
      toast.error(t('settings.highlight_tags.toast_color_taken'));
      return;
    }
    if (!editing && firstFreeHighlightTagColor(tags) === null) {
      toast.error(t('settings.highlight_tags.toast_color_taken'));
      return;
    }
    try {
      if (editing) {
        await update.mutateAsync({
          id: editing.id,
          name,
          color_token: color,
        });
        toast.success(t('settings.highlight_tags.toast_updated'));
      } else {
        await create.mutateAsync({ name, color_token: color });
        toast.success(t('settings.highlight_tags.toast_created'));
      }
      closeForm();
    } catch (err) {
      saveError(err);
    }
  };

  const move = async (row: DocumentHighlightTagRow, direction: -1 | 1) => {
    const index = active.findIndex((item) => item.id === row.id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= active.length) return;
    const next = [...active];
    const [moved] = next.splice(index, 1);
    if (!moved) return;
    next.splice(target, 0, moved);
    try {
      await reorder.mutateAsync(
        next.map((item, position) => ({ id: item.id, sort_order: (position + 1) * 10 })),
      );
    } catch (err) {
      saveError(err);
    }
  };

  const setArchived = async (row: DocumentHighlightTagRow, isArchived: boolean) => {
    if (!isArchived && occupiedHighlightTagColors(tags).has(row.color_token)) {
      toast.error(t('settings.highlight_tags.toast_color_taken'));
      return;
    }
    try {
      await update.mutateAsync({ id: row.id, is_archived: isArchived });
      toast.success(
        isArchived
          ? t('settings.highlight_tags.toast_archived')
          : t('settings.highlight_tags.toast_restored'),
      );
    } catch (err) {
      saveError(err);
    }
  };

  return (
    <SectionCard
      title={t('settings.highlight_tags.title')}
      subtitle={t('settings.highlight_tags.subtitle')}
      readOnly={!canManage}
      action={
        canManage && active.length > 0 ? (
          <Button onClick={openCreate} className="w-full nav:w-auto">
            <Plus className="h-4 w-4" aria-hidden />
            {t('settings.highlight_tags.new')}
          </Button>
        ) : undefined
      }
    >
      {!tagsQuery.isLoading && duplicates ? (
        <p className={cn(TYPE.small, 'mb-s3 text-ds-amber-ink')}>
          {t('settings.highlight_tags.duplicate_warning')}
        </p>
      ) : null}
      {tagsQuery.isLoading ? (
        <SettingsLoading />
      ) : active.length === 0 && archived.length === 0 ? (
        <SettingsEmpty
          icon={<Tag className="h-7 w-7" />}
          title={t('settings.highlight_tags.empty')}
          action={
            canManage ? (
              <Button onClick={openCreate} className="w-full">
                <Plus className="h-4 w-4" aria-hidden />
                {t('settings.highlight_tags.new')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="-mx-s4 nav:-mx-s5">
          {active.map((row, index) => (
            <ListRow key={row.id} className="justify-between">
              <div className="flex min-w-0 flex-1 items-center gap-s3">
                <HighlightTagFace tag={row} />
              </div>
              {canManage ? (
                <div className="flex shrink-0 gap-s1">
                  <Button
                    type="button"
                    variant="tertiary"
                    size="icon"
                    disabled={index === 0 || reorder.isPending}
                    onClick={() => void move(row, -1)}
                    aria-label={t('settings.highlight_tags.move_up')}
                  >
                    <ArrowUp />
                  </Button>
                  <Button
                    type="button"
                    variant="tertiary"
                    size="icon"
                    disabled={index === active.length - 1 || reorder.isPending}
                    onClick={() => void move(row, 1)}
                    aria-label={t('settings.highlight_tags.move_down')}
                  >
                    <ArrowDown />
                  </Button>
                  <Button
                    type="button"
                    variant="tertiary"
                    size="icon"
                    onClick={() => openEdit(row)}
                    aria-label={t('common.edit')}
                  >
                    <Pencil />
                  </Button>
                  <Button
                    type="button"
                    variant="tertiary"
                    size="icon"
                    onClick={() => void setArchived(row, true)}
                    aria-label={t('settings.highlight_tags.archive')}
                  >
                    <Archive />
                  </Button>
                </div>
              ) : null}
            </ListRow>
          ))}
        </div>
      )}

      {archived.length > 0 ? (
        <div className="mt-s5">
          <h3 className={cn(TYPE.label, 'mb-s2 text-ds-muted')}>
            {t('settings.highlight_tags.archived')}
          </h3>
          <div className="-mx-s4 nav:-mx-s5">
            {archived.map((row) => (
              <ListRow key={row.id} className="justify-between">
                <HighlightTagFace tag={row} />
                {canManage ? (
                  <Button
                    type="button"
                    variant="tertiary"
                    size="icon"
                    onClick={() => void setArchived(row, false)}
                    aria-label={t('settings.highlight_tags.restore')}
                  >
                    <RotateCcw />
                  </Button>
                ) : null}
              </ListRow>
            ))}
          </div>
        </div>
      ) : null}

      {showForm ? (
        <Modal
          title={editing ? t('settings.highlight_tags.edit') : t('settings.highlight_tags.new')}
          onClose={closeForm}
        >
          <form onSubmit={(event) => void onSubmit(event)} className="space-y-s4">
            <Input
              label={t('settings.highlight_tags.field_name')}
              hint={t('settings.highlight_tags.field_name_hint')}
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={80}
            />
            <ColorChoices
              tags={active}
              editingId={editing?.id}
              color={color}
              onColor={(token) => setColor(token)}
            />
            <div className="flex justify-end gap-s2 pt-s2">
              <Button type="button" variant="secondary" onClick={closeForm}>
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={
                  create.isPending ||
                  update.isPending ||
                  (!editing && firstFreeHighlightTagColor(tags) === null)
                }
              >
                {t('common.save')}
              </Button>
            </div>
          </form>
        </Modal>
      ) : null}
    </SectionCard>
  );
}
